import React, { FC } from 'react';
import styles from './ZipModal.module.scss';
import { StructuredModal, StructuredModalState } from '../StructuredModal/StructuredModal';
import cx from 'classnames';
import { TextV2 } from 'components/TextV2/TextV2';
import { HardwarePayment } from 'components/Cart/OrderSummary/HardwarePayment/HardwarePayment';
import { useTranslation } from 'react-i18next';
import { SoftwarePlan } from 'components/Cart/OrderSummary/SoftwarePlan/SoftwarePlan';
import { LinkVariant } from 'api/contentful/contentTypes.cloverdotcom';
import { useCheckout } from 'components/Checkout/CheckoutProvider';
import { getCartProductsAndAccessories } from 'components/Cart/utils/getCartProductsAndAccessoriesUtils';
import { onContinueClick } from 'utils/ZipModalUtils';
import { useCartContext } from 'components/Cart/hooks/useCartContext';
import { useFeatureFlagsContext } from 'components/FeatureFlags/FeatureFlags';
import { onModalContactSalesClick } from 'utils/ContactSalesUtils';

interface IZipModalProp {
  modalState: StructuredModalState;
  onContinue: () => void;
  isServiceUnavailable?: boolean;
}

export const ZipModal: FC<IZipModalProp> = ({ modalState, onContinue, isServiceUnavailable = false }) => {
  const { t } = useTranslation();
  const { cart } = useCartContext();
  const { isFlagEnabled } = useFeatureFlagsContext();
  const { setSelectedPurchaseType } = useCheckout();
  const { allCartProductsAndAccessories } = getCartProductsAndAccessories(cart);
  const { softwarePlan } = cart;

  const isShippingFeeEnabled = isFlagEnabled('shipping-fee-enabled');

  const additionalDeviceFee = t('cart-page.monthly-price', { price: softwarePlan?.additionalDevicePrice });

  const totalCost = allCartProductsAndAccessories?.reduce((accumulator, item) => {
    const itemCost = item.totalCost * item.quantity;

    return accumulator + itemCost;
  }, 0);
  const shippingFee = 9.99;
  const totalCostWithShippingFee = isShippingFeeEnabled ? totalCost + shippingFee : totalCost;

  return (
    <StructuredModal
      modalState={modalState}
      aria={{ modal: true, labelledby: 'zipModal' }}
      className={styles.modal}
      actions={[
        {
          label: t('contact-sales'),
          variant: LinkVariant.SECONDARY,
          action: onModalContactSalesClick,
        },
        {
          label: t('zip-modal.primary-button'),
          variant: LinkVariant.PRIMARY,
          action: () => onContinueClick(setSelectedPurchaseType, onContinue),
        },
      ]}
    >
      <div className={cx(styles.modalContainer)}>
        <TextV2 variant="h3-bold" tagName="h3" value={t('zip-modal.title')} />
        <TextV2
          variant="body"
          tagName="p"
          value={isServiceUnavailable ? t('zip-modal.service-unavailable') : t('zip-modal.subtitle')}
          className={styles.subtitle}
        />
        <div className={styles.modalContent}>
          <div className={styles.dueTodaySection}>
            <HardwarePayment isVermont />
            <div className={styles.textFieldWrapper}>
              <TextV2 variant="body" tagName="p" value={t('order-summary.shipping')} />
              <TextV2
                variant="body"
                tagName="p"
                value={isShippingFeeEnabled ? t('order-summary.shipping-fee') : t('order-summary.free')}
              />
            </div>
            <div className={styles.textFieldWrapper}>
              <TextV2 variant="body" tagName="p" value={t('order-summary.tax-text')} />
              <TextV2 variant="body" tagName="p" value={t('order-summary.tax-text-value')} />
            </div>
            <span className={styles.borderBottom} />
          </div>
          <div className={styles.softwarePlanWrapper}>
            <SoftwarePlan />
            <TextV2
              variant="small"
              tagName="p"
              value={t('cart-page.additional-device-price', { additionalDeviceFee })}
              className={styles.softwarePlanSubText}
            />
            <span className={styles.borderBottom} />
          </div>
          <div className={cx(styles.textFieldWrapper, styles.dueApproval)}>
            <TextV2 variant="h5-medium" tagName="h5" value={t('zip-modal.due-approval')} />
            <TextV2
              variant="h5-medium"
              tagName="h5"
              value={t('order-summary.price', { price: totalCostWithShippingFee })}
            />
          </div>
        </div>
      </div>
    </StructuredModal>
  );
};
